/**
 * reCAPTCHA v3 — must match Interlinked-Frontend / platform-api (not v2 invisible).
 * Site key and RECAPTCHA_SECRET_KEY must be from the same v3 key pair in Google console.
 */

const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY || '';

export const CAPTCHA_DISABLED =
  process.env.REACT_APP_DISABLE_CAPTCHA === 'true'
  || (process.env.NODE_ENV === 'development' && !CAPTCHA_SITE_KEY);

let scriptLoadPromise = null;

function loadRecaptchaScript() {
  if (CAPTCHA_DISABLED || !CAPTCHA_SITE_KEY) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  if (typeof window !== 'undefined' && window.grecaptcha?.execute) {
    scriptLoadPromise = new Promise((resolve) => {
      window.grecaptcha.ready(resolve);
    });
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existing) {
      window.grecaptcha.ready(resolve);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${CAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(resolve);
    };
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function preloadRecaptcha() {
  return loadRecaptchaScript();
}

export async function executeRecaptcha(action) {
  if (CAPTCHA_DISABLED) {
    return 'disabled';
  }

  if (!CAPTCHA_SITE_KEY) {
    throw new Error('Captcha is required but REACT_APP_CAPTCHA_SITE_KEY is not configured');
  }

  await loadRecaptchaScript();

  if (!window.grecaptcha?.execute) {
    throw new Error('reCAPTCHA is not ready yet');
  }

  const token = await window.grecaptcha.execute(CAPTCHA_SITE_KEY, { action });
  if (!token) {
    throw new Error(`Captcha verification failed (${action})`);
  }
  return token;
}
